/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import './index.scss';

import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import { BrowserRouter, MemoryRouter } from 'react-router-dom';
import { BrowserTracing, init as sentryInit } from '@sentry/react';

import GitCommit from './_git_commit';
import App from './App';
import { store } from './app/store';
import { sentryConfig } from './sentry_config';
import * as serviceWorker from './serviceWorker';
import { oktaAuthConfig } from './features/sharedcomponents/okta_config';
import { ErrorScreen } from './features/sharedcomponents/ErrorScreen';

if (sentryConfig?.enabled) {
  sentryInit({
    dsn: sentryConfig.dsn,
    ignoreErrors: ['AbortError', 'ConditionError'],
    integrations: [new BrowserTracing()],
    release: 'pearl-consumer@' + GitCommit.logMessage,
    normalizeDepth: 5, // anything over 5 causes ui and user keys to get dropped in Sentry logs (known Sentry bug)
    environment: process.env.REACT_APP_ENVIRONMENT || 'development',
  });
}

window.store = store;

/**
 * Detect whether the app is running inside an iframe.
 * Safe for cross-origin frames.
 */
function detectIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true; // If we can't access window.top, we're inside an iframe
  }
}

const isIframe = detectIframe();
const RouterComponent = isIframe ? MemoryRouter : BrowserRouter;

console.debug(`App launch context: ${isIframe ? 'embedded (iframe)' : 'standalone tab'}`);

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Unable to find the root element');
}
const root = createRoot(rootElement);

// Get the per-client okta application id before render
// so we know where to send this user for login

void oktaAuthConfig(window.location.hostname.toLowerCase() === 'app.medpearl.com' ? 'app.medpearl.com' : 'consumer')
  .then((authConfig) =>
    root.render(
      <Provider store={store}>
        <RouterComponent>
          <App {...authConfig} />
        </RouterComponent>
      </Provider>
    )
  )
  .catch((e) => {
    root.render(
      <React.StrictMode>
        <div style={{ margin: 30 }}>
          <h1>Connecting to server...</h1>
          <div style={{ margin: 50 }} />
          <ErrorScreen error={e} />
        </div>
      </React.StrictMode>
    );
  });

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
